import { useState } from 'react';
import { getValidSingleCellAddress } from 'shared/excel/excel';
import ContextStore from 'src/stores/contextStore';

import { InputValidation, RangeValidation, WorksheetValidation } from '../types';

export const NEW_WORKSHEET_ID = 'new-worksheet-id'; // Unique ID for the new worksheet

const NEW_WORKSHEET = {
  id: NEW_WORKSHEET_ID,
  name: '{New Worksheet}',
};

export function useCellAddressValidation(contextStore: ContextStore, allowEmptyCell: boolean, allowNewSheet: boolean) {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const getWorksheet = ({ id, name }: { id?: string; name?: string }): WorksheetValidation => {
    if (!id && !name) {
      return { isValid: true };
    }

    if (allowNewSheet && (id === NEW_WORKSHEET_ID || name === NEW_WORKSHEET.name)) {
      return {
        worksheet: NEW_WORKSHEET,
        isValid: true,
      };
    }

    const worksheet = contextStore.getWorksheet({ name, id });
    return worksheet
      ? {
          worksheet,
          isValid: true,
        }
      : { isValid: false };
  };

  const getCustomRange = (rangeName: string): RangeValidation => {
    if (!rangeName) {
      return { isValid: true };
    }

    const range = contextStore.getNamedRangeAddress(rangeName);
    return range
      ? {
          range,
          isValid: true,
        }
      : { isValid: false };
  };

  const validateInput = (value: string): InputValidation => {
    const result: Partial<InputValidation> = { isEmpty: !value };
    let errorMsg: string | null = null;

    if (result.isEmpty) {
      if (!allowEmptyCell) {
        errorMsg = 'Empty address';
      }
    } else {
      const rangeValidation = getCustomRange(value);
      if (rangeValidation.range && rangeValidation.isValid) {
        result.rangeName = value;
      } else {
        const cell = getValidSingleCellAddress(value);
        if (!cell?.first) {
          errorMsg = 'Incorrect address';
        } else {
          result.address = cell.first;
          if (cell?.sheet) {
            const sheetValidation = getWorksheet({ name: cell.sheet });
            if (!sheetValidation.isValid) {
              errorMsg = 'Worksheet not found';
            } else {
              result.worksheetId = sheetValidation.worksheet?.id;
            }
          }
        }
      }
    }

    setErrorMessage(errorMsg);
    return {
      ...result,
      isValid: !errorMsg,
    } as InputValidation;
  };

  return {
    errorMessage,
    setErrorMessage,
    validateInput,
    getWorksheet,
    getCustomRange,
  };
}
